import { t } from "@lingui/macro";
import { Check, CaretDown } from "@phosphor-icons/react";
import { basicsSchema } from "@reactive-resume/schema";
import { cn } from "@reactive-resume/utils";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
} from "@reactive-resume/ui";
import { useEffect, useMemo, useState } from "react";

import { useResumeStore } from "@/client/stores/resume";

import { CustomFieldsSection } from "./custom/section";
import { PictureSection } from "./picture/section";
import { getSectionIcon } from "./shared/section-icon";
import { URLInput } from "./shared/url-input";

const countryCodes = [
  { name: "India", code: "+91" },
  { name: "United States", code: "+1" },
  { name: "Canada", code: "+1" },
  { name: "United Kingdom", code: "+44" },
  { name: "Australia", code: "+61" },
  { name: "Singapore", code: "+65" },
  { name: "United Arab Emirates", code: "+971" },
  { name: "Saudi Arabia", code: "+966" },
  { name: "Germany", code: "+49" },
  { name: "France", code: "+33" },
  { name: "Spain", code: "+34" },
  { name: "Italy", code: "+39" },
  { name: "Netherlands", code: "+31" },
  { name: "Switzerland", code: "+41" },
  { name: "Sweden", code: "+46" },
  { name: "Norway", code: "+47" },
  { name: "Denmark", code: "+45" },
  { name: "Ireland", code: "+353" },
  { name: "Belgium", code: "+32" },
  { name: "Austria", code: "+43" },
  { name: "Portugal", code: "+351" },
  { name: "Poland", code: "+48" },
  { name: "Czech Republic", code: "+420" },
  { name: "Hungary", code: "+36" },
  { name: "Romania", code: "+40" },
  { name: "Greece", code: "+30" },
  { name: "Turkey", code: "+90" },
  { name: "South Africa", code: "+27" },
  { name: "Nigeria", code: "+234" },
  { name: "Kenya", code: "+254" },
  { name: "Egypt", code: "+20" },
  { name: "China", code: "+86" },
  { name: "Japan", code: "+81" },
  { name: "South Korea", code: "+82" },
  { name: "Hong Kong", code: "+852" },
  { name: "Taiwan", code: "+886" },
  { name: "Thailand", code: "+66" },
  { name: "Vietnam", code: "+84" },
  { name: "Malaysia", code: "+60" },
  { name: "Indonesia", code: "+62" },
  { name: "Philippines", code: "+63" },
  { name: "Pakistan", code: "+92" },
  { name: "Bangladesh", code: "+880" },
  { name: "Sri Lanka", code: "+94" },
  { name: "Nepal", code: "+977" },
  { name: "Brazil", code: "+55" },
  { name: "Mexico", code: "+52" },
  { name: "Argentina", code: "+54" },
  { name: "Chile", code: "+56" },
  { name: "Colombia", code: "+57" },
  { name: "Peru", code: "+51" },
  { name: "New Zealand", code: "+64" },
  {name: "Kuwait", code: "+965"},
  { name: "Latvia", code: "+371" },
];

const defaultCountryCode = "+91";

const getPhoneParts = (phone: string) => {
  const value = phone.trim();
  const selected =
    countryCodes
      .slice()
      .sort((a, b) => b.code.length - a.code.length)
      .find((country) => value === country.code || value.startsWith(`${country.code} `)) ??
    countryCodes[0];

  const localNumber = value.startsWith(selected.code)
    ? value.slice(selected.code.length).trimStart()
    : value.replace(/^\+\d+\s*/, "");

  return { selected, localNumber };
};

/** Basics LinkedIn / Portfolio use label+URL popover (swap) only on these templates. */
const BASICS_SWAP_LINK_FIELD_TEMPLATES = new Set([
  "Create Custom",
  "LFBA_LFMS",
  "LFTS 1(GEN)",
  "LFTS 2(SPL)",
]);

export const BasicsSection = () => {
  const [isPhoneCodeOpen, setIsPhoneCodeOpen] = useState(false);
  const setValue = useResumeStore((state) => state.setValue);
  const basics = useResumeStore((state) => state.resume.data.basics);
  const currentTemplate = useResumeStore((state) => state.resume.data.metadata.template);
  const isAdvancedTemplate = currentTemplate === "mastersunion";
  const useSwappedBasicsLinkFields = BASICS_SWAP_LINK_FIELD_TEMPLATES.has(currentTemplate);

  useEffect(() => {
    if (!basics.phone.trim()) {
      setValue("basics.phone", `${defaultCountryCode} `);
    }
  }, [basics.phone, setValue]);

  const phoneParts = useMemo(() => getPhoneParts(basics.phone), [basics.phone]);

  const updatePhone = (countryCode: string, localNumber: string) => {
    const trimmedNumber = localNumber.trimStart();
    setValue("basics.phone", trimmedNumber ? `${countryCode} ${trimmedNumber}` : `${countryCode} `);
  };

  return (
    <section id="basics" className="grid gap-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-x-4">
          {getSectionIcon("basics")}
          <h2 className="line-clamp-1 text-3xl font-bold">{t`Basics`}</h2>
        </div>
      </header>

      <main className="grid gap-4 sm:grid-cols-2">
        {!isAdvancedTemplate ? <div className="sm:col-span-2">
          <PictureSection />
        </div> : null}

        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="basics.name">{t`Full Name`}</Label>
          <Input
            id="basics.name"
            value={basics.name}
            hasError={!basicsSchema.pick({ name: true }).safeParse({ name: basics.name }).success}
            onChange={(event) => setValue("basics.name", event.target.value)}
            className="uppercase"
          />
        </div>

        {!isAdvancedTemplate ? <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="basics.headline">{t`Headline`}</Label>
          <Input
            id="basics.headline"
            value={basics.headline}
            onChange={(event) => setValue("basics.headline", event.target.value)}
          />
        </div> : null}

        <div className="space-y-1.5">
          <Label htmlFor="basics.email">{t`Email`}</Label>
          <Input
            id="basics.email"
            placeholder="john.doe@example.com"
            value={basics.email}
            hasError={
              !basicsSchema.pick({ email: true }).safeParse({ email: basics.email }).success
            }
            onChange={(event) => setValue("basics.email", event.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="basics.url">LinkedIn</Label>
          <URLInput
            id="basics.url"
            // hideLabelIcon={isAdvancedTemplate}
            {...(useSwappedBasicsLinkFields
              ? {
                  value: {
                    ...basics.url,
                    label:
                      (typeof basics.url.label === "string" && basics.url.label.trim()) ||
                      (basics.url.href?.trim() ? 'LinkedIn' : ""),
                  },
                  placeholder: 'LinkedIn',
                  labelPlaceholder: 'https://linkedin.com/in/username',
                  disableAutoHttps: true,
                  swapLabelAndHref: true as const,
                }
              : {
                  value: basics.url,
                  placeholder: "https://linkedin.com/in/username",
                })}
            onChange={(value) => setValue("basics.url", value)}
          />
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="basics.portfolio">Portfolio Link</Label>
          <URLInput
            id="basics.portfolio"
            // hideLabelIcon={isAdvancedTemplate}
            {...(useSwappedBasicsLinkFields
              ? {
                  value: {
                    ...(basics.portfolio ?? { label: "", href: "" }),
                    label:
                      (typeof basics.portfolio?.label === "string" && basics.portfolio.label.trim()) ||
                      (basics.portfolio?.href?.trim() ? 'Portfolio' : ""),
                  },
                  placeholder: 'Portfolio',
                  labelPlaceholder: 'https://portfolio.example.com',
                  disableAutoHttps: true,
                  swapLabelAndHref: true as const,
                }
              : {
                  value: {
                    ...(basics.portfolio ?? { href: "", label: "" }),
                    label: isAdvancedTemplate
                      ? (typeof basics.portfolio?.label === "string" ? basics.portfolio.label.trim() : "")
                      : basics.portfolio?.label || 'Portfolio',
                  },
                  placeholder: "https://portfolio.example.com",
                })}
            onChange={(value) => setValue("basics.portfolio", value)}
          />
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="basics.phone">{t`Phone`}</Label>
          <div className="flex gap-2">
            <Popover open={isPhoneCodeOpen} onOpenChange={setIsPhoneCodeOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={isPhoneCodeOpen}
                  className="w-32 justify-between"
                >
                  <span>{phoneParts.selected.code}</span>
                  <CaretDown className="ml-2 size-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 p-0">
                <Command>
                  <CommandInput placeholder="Search country or code" />
                  <CommandEmpty>No country found.</CommandEmpty>
                  <CommandGroup>
                    <ScrollArea orientation="vertical">
                      <div className="max-h-60">
                        {countryCodes.map((country) => (
                          <CommandItem
                            key={`${country.name}-${country.code}`}
                            value={`${country.name} ${country.code}`}
                            onSelect={() => {
                              updatePhone(country.code, phoneParts.localNumber);
                              setIsPhoneCodeOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 size-4 opacity-0",
                                phoneParts.selected.code === country.code &&
                                  phoneParts.selected.name === country.name &&
                                  "opacity-100",
                              )}
                            />
                            <span className="flex-1">{country.name}</span>
                            <span className="text-xs opacity-70">{country.code}</span>
                          </CommandItem>
                        ))}
                      </div>
                    </ScrollArea>
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>

            <Input
              id="basics.phone"
              className="flex-1"
              placeholder="9876543210"
              value={phoneParts.localNumber}
              onChange={(event) => updatePhone(phoneParts.selected.code, event.target.value)}
            />
          </div>
        </div>

        {!isAdvancedTemplate ? <div className="space-y-1.5">
          <Label htmlFor="basics.location">{t`Location`}</Label>
          <Input
            id="basics.location"
            value={basics.location}
            onChange={(event) => setValue("basics.location", event.target.value)}
          />
        </div> : null}

        <CustomFieldsSection className="sm:col-span-2" />
      </main>
    </section>
  );
};
